import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Row, Col, Spinner, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import client from '../api/client';
import { fmtMoney, fmtDate, fmtNum } from '../utils/format';
import { useAuth } from '../context/AuthContext';

const PAYMENT_MODES = ['cash', 'pdc', 'cheque', 'neft', 'rtgs', 'upi', 'card', 'other'];

const emptyPayment = {
  amount: 0,
  mode: 'neft',
  bucket: 'account',
  status: 'received',
  referenceNo: '',
  bankName: '',
  receivedDate: new Date().toISOString().slice(0, 10),
  targetDate: '',
  dueDate: '',
  notes: '',
};

const BookingDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [payments, setPayments] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPay, setShowPay] = useState(false);
  const [payForm, setPayForm] = useState(emptyPayment);

  const [showCom, setShowCom] = useState(false);
  const [comForm, setComForm] = useState({
    amount: 0,
    status: 'pending',
    mode: 'neft',
    targetDate: '',
    paidOn: '',
    referenceNo: '',
    notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: pays }, { data: coms }] = await Promise.all([
        client.get(`/bookings/${id}`),
        client.get('/payments', { params: { booking: id } }),
        client.get('/commissions', { params: { booking: id } }),
      ]);
      setBooking(b);
      setPayments(pays);
      setCommissions(coms);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addPayment = async (e) => {
    e.preventDefault();
    const payload = { ...payForm, booking: id };
    if (!payload.targetDate) delete payload.targetDate;
    if (!payload.dueDate) delete payload.dueDate;
    try {
      await client.post('/payments', payload);
      toast.success('Payment recorded');
      setShowPay(false);
      setPayForm(emptyPayment);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.message || 'Save failed');
    }
  };

  const addCommission = async (e) => {
    e.preventDefault();
    const payload = {
      ...comForm,
      booking: id,
      salesAgent: booking.salesAgent?._id || booking.salesAgent,
    };
    if (!payload.targetDate) delete payload.targetDate;
    if (!payload.paidOn) delete payload.paidOn;
    try {
      await client.post('/commissions', payload);
      toast.success('Commission record added');
      setShowCom(false);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.message || 'Save failed');
    }
  };

  const setPaid = async (c) => {
    try {
      await client.put(`/commissions/${c._id}`, { status: 'paid', paidOn: new Date() });
      toast.success('Marked paid');
      load();
    } catch (ex) {
      toast.error('Failed');
    }
  };

  if (loading) return <Spinner animation="border" />;
  if (!booking) return <div>Not found</div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Link to="/bookings" className="btn btn-sm btn-light"><i className="bi bi-arrow-left"></i> Back</Link>
          <h4 className="mb-0 mt-2">Booking {booking.bookingNo}</h4>
          <div className="text-muted">
            <Badge bg="info" className="me-2">{booking.status}</Badge>
            {fmtDate(booking.bookingDate)}
          </div>
        </div>
      </div>

      <Row className="g-3">
        <Col md={6}>
          <Card>
            <Card.Header><strong>Property</strong></Card.Header>
            <Card.Body>
              <div><strong>Project:</strong> {booking.project?.name} ({booking.project?.code})</div>
              <div><strong>Unit No:</strong> {booking.unitNo} {booking.tower && `· Tower ${booking.tower}`} {booking.floor && `· Floor ${booking.floor}`}</div>
              <div><strong>Type:</strong> {booking.unitType || '-'}</div>
              <div><strong>Area:</strong> {fmtNum(booking.areaSqft)} sqft</div>
              <div><strong>Booking Rate:</strong> {fmtMoney(booking.bookingRatePerSqft)} /sqft</div>
              <div><strong>Actual Rate:</strong> {fmtMoney(booking.actualRatePerSqft)} /sqft</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header><strong>Customer & Agent</strong></Card.Header>
            <Card.Body>
              <div><strong>{booking.customerName}</strong></div>
              <div className="text-muted">{booking.customerPhone} · {booking.customerEmail || '-'}</div>
              <div className="text-muted">{booking.customerAddress}</div>
              <hr />
              <div><strong>Sales Agent:</strong> {booking.salesAgent?.name}</div>
              <div className="text-muted">{booking.salesAgent?.email}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Header><strong>Money</strong></Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={3}><div className="text-muted small">Total Booking Value</div><h5>{fmtMoney(booking.totalBookingValue)}</h5></Col>
            <Col md={3}><div className="text-muted small">Total Actual Value</div><h5>{fmtMoney(booking.totalActualValue)}</h5></Col>
            <Col md={3}><div className="text-muted small">Other Charges</div><h5>{fmtMoney(booking.otherCharges)}</h5></Col>
            <Col md={3}><div className="text-muted small">Discount</div><h5>{fmtMoney(booking.discount)}</h5></Col>
            <Col md={3}><div className="text-muted small">Grand Total</div><h4 className="text-primary">{fmtMoney(booking.grandTotal)}</h4></Col>
            <Col md={3}><div className="text-muted small">Received in Account</div><h5 className="text-success">{fmtMoney(booking.paymentReceivedAccount)}</h5></Col>
            <Col md={3}><div className="text-muted small">Received in PDC/Cash</div><h5 className="text-info">{fmtMoney(booking.paymentReceivedPDC)}</h5></Col>
            <Col md={3}><div className="text-muted small">Total Received</div><h5 className="text-success">{fmtMoney(booking.paymentTotalReceived)}</h5></Col>
            <Col md={3}><div className="text-muted small">Pending</div><h5 className="text-warning">{fmtMoney(booking.paymentPending)}</h5></Col>
            <Col md={3}><div className="text-muted small">Overdue (Due)</div><h5 className="text-danger">{fmtMoney(booking.paymentDue)}</h5></Col>
            <Col md={3}><div className="text-muted small">Next Target Date</div><h5>{fmtDate(booking.nextPaymentTargetDate)}</h5></Col>
            <Col md={3}><div className="text-muted small">Commission</div>
              <div>Total: <strong>{fmtMoney(booking.commissionAmount)}</strong></div>
              <div>Paid: <span className="text-success">{fmtMoney(booking.commissionPaid)}</span></div>
              <div>Pending: <span className="text-warning">{fmtMoney(booking.commissionPending)}</span></div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between">
          <strong>Payments</strong>
          {['admin', 'sales', 'support'].includes(user.role) && (
            <Button size="sm" onClick={() => setShowPay(true)}><i className="bi bi-plus-lg"></i> Record Payment</Button>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive size="sm" className="mb-0">
            <thead><tr>
              <th>Date</th><th>Mode</th><th>Bucket</th><th>Amount</th><th>Status</th>
              <th>Reference</th><th>Target Date</th><th>Due Date</th>
            </tr></thead>
            <tbody>
              {payments.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-3">No payments yet</td></tr>}
              {payments.map((p) => (
                <tr key={p._id}>
                  <td>{fmtDate(p.receivedDate)}</td>
                  <td className="text-uppercase">{p.mode}</td>
                  <td>
                    <Badge bg={p.bucket === 'account' ? 'success' : 'info'} className="text-uppercase">{p.bucket}</Badge>
                  </td>
                  <td>{fmtMoney(p.amount)}</td>
                  <td><Badge bg={p.status === 'cleared' ? 'success' : p.status === 'pending' ? 'warning' : p.status === 'bounced' ? 'danger' : 'info'} className="text-uppercase">{p.status}</Badge></td>
                  <td>{p.referenceNo || '-'}<br /><small className="text-muted">{p.bankName}</small></td>
                  <td>{fmtDate(p.targetDate)}</td>
                  <td>{fmtDate(p.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between">
          <strong>Sales Agent Commission</strong>
          {['admin', 'support'].includes(user.role) && (
            <Button size="sm" onClick={() => setShowCom(true)}><i className="bi bi-plus-lg"></i> Add Commission</Button>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive size="sm" className="mb-0">
            <thead><tr>
              <th>Status</th><th>Amount</th><th>Mode</th><th>Target Date</th>
              <th>Paid On</th><th>Reference</th><th></th>
            </tr></thead>
            <tbody>
              {commissions.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-3">No commission records</td></tr>}
              {commissions.map((c) => (
                <tr key={c._id}>
                  <td><Badge bg={c.status === 'paid' ? 'success' : c.status === 'pending' ? 'warning' : 'secondary'}>{c.status}</Badge></td>
                  <td>{fmtMoney(c.amount)}</td>
                  <td className="text-uppercase">{c.mode}</td>
                  <td>{fmtDate(c.targetDate)}</td>
                  <td>{fmtDate(c.paidOn)}</td>
                  <td>{c.referenceNo || '-'}</td>
                  <td className="text-end">
                    {c.status === 'pending' && ['admin', 'support'].includes(user.role) && (
                      <Button size="sm" variant="outline-success" onClick={() => setPaid(c)}>Mark Paid</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Payment modal */}
      <Modal show={showPay} onHide={() => setShowPay(false)}>
        <Form onSubmit={addPayment}>
          <Modal.Header closeButton><Modal.Title>Record Payment</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6"><Form.Label>Amount *</Form.Label>
                <Form.Control required type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: Number(e.target.value) })} /></div>
              <div className="col-md-6"><Form.Label>Date</Form.Label>
                <Form.Control type="date" value={payForm.receivedDate} onChange={(e) => setPayForm({ ...payForm, receivedDate: e.target.value })} /></div>
              <div className="col-md-4"><Form.Label>Mode *</Form.Label>
                <Form.Select value={payForm.mode} onChange={(e) => {
                  const mode = e.target.value;
                  const bucket = ['cash', 'pdc', 'cheque'].includes(mode) ? 'pdc' : 'account';
                  setPayForm({ ...payForm, mode, bucket });
                }}>{PAYMENT_MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}</Form.Select></div>
              <div className="col-md-4"><Form.Label>Bucket</Form.Label>
                <Form.Select value={payForm.bucket} onChange={(e) => setPayForm({ ...payForm, bucket: e.target.value })}>
                  <option value="account">Account (cleared bank)</option>
                  <option value="pdc">PDC / Cash</option>
                </Form.Select></div>
              <div className="col-md-4"><Form.Label>Status</Form.Label>
                <Form.Select value={payForm.status} onChange={(e) => setPayForm({ ...payForm, status: e.target.value })}>
                  <option value="received">Received</option>
                  <option value="cleared">Cleared</option>
                  <option value="pending">Pending</option>
                  <option value="bounced">Bounced</option>
                </Form.Select></div>
              <div className="col-md-6"><Form.Label>Reference No</Form.Label>
                <Form.Control value={payForm.referenceNo} onChange={(e) => setPayForm({ ...payForm, referenceNo: e.target.value })} /></div>
              <div className="col-md-6"><Form.Label>Bank</Form.Label>
                <Form.Control value={payForm.bankName} onChange={(e) => setPayForm({ ...payForm, bankName: e.target.value })} /></div>
              <div className="col-md-6"><Form.Label>Target Date (expected)</Form.Label>
                <Form.Control type="date" value={payForm.targetDate} onChange={(e) => setPayForm({ ...payForm, targetDate: e.target.value })} /></div>
              <div className="col-md-6"><Form.Label>Due Date (contractual)</Form.Label>
                <Form.Control type="date" value={payForm.dueDate} onChange={(e) => setPayForm({ ...payForm, dueDate: e.target.value })} /></div>
              <div className="col-12"><Form.Label>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPay(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Commission modal */}
      <Modal show={showCom} onHide={() => setShowCom(false)}>
        <Form onSubmit={addCommission}>
          <Modal.Header closeButton><Modal.Title>Add Commission Record</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6"><Form.Label>Amount *</Form.Label>
                <Form.Control required type="number" value={comForm.amount} onChange={(e) => setComForm({ ...comForm, amount: Number(e.target.value) })} /></div>
              <div className="col-md-6"><Form.Label>Status</Form.Label>
                <Form.Select value={comForm.status} onChange={(e) => setComForm({ ...comForm, status: e.target.value })}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select></div>
              <div className="col-md-6"><Form.Label>Target Date</Form.Label>
                <Form.Control type="date" value={comForm.targetDate} onChange={(e) => setComForm({ ...comForm, targetDate: e.target.value })} /></div>
              <div className="col-md-6"><Form.Label>Paid On</Form.Label>
                <Form.Control type="date" value={comForm.paidOn} onChange={(e) => setComForm({ ...comForm, paidOn: e.target.value })} /></div>
              <div className="col-md-6"><Form.Label>Mode</Form.Label>
                <Form.Select value={comForm.mode} onChange={(e) => setComForm({ ...comForm, mode: e.target.value })}>
                  <option value="neft">NEFT</option><option value="rtgs">RTGS</option><option value="upi">UPI</option>
                  <option value="cheque">Cheque</option><option value="cash">Cash</option><option value="other">Other</option>
                </Form.Select></div>
              <div className="col-md-6"><Form.Label>Reference No</Form.Label>
                <Form.Control value={comForm.referenceNo} onChange={(e) => setComForm({ ...comForm, referenceNo: e.target.value })} /></div>
              <div className="col-12"><Form.Label>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} value={comForm.notes} onChange={(e) => setComForm({ ...comForm, notes: e.target.value })} /></div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCom(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BookingDetail;
